import { useEffect, useState } from "react";
import { Star, Target, CalendarClock, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function YourStatePanel({ state }) {
  // URL slugs arrive lowercase — display proper-cased
  const displayState = (state || "").replace(/[-_]/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
  const { isAuthed } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthed || !state) { setData(null); return; }
    api.stateSummary(state).then(setData).catch(() => setData(null));
  }, [isAuthed, state]);

  if (!isAuthed || !data) return null;

  const hasSignal = data.following || data.points.length || data.total_hunts;
  if (!hasSignal) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-4">
        <div className="flex items-center justify-between gap-4 bg-forest/[0.06] border border-forest/20 rounded-lg px-5 py-4" data-testid="your-state-panel">
          <p className="text-sm text-charcoal/70"><span className="font-semibold text-forest">Follow {displayState}</span> to keep its seasons, regs and draw deadlines in your HomeRange.</p>
          <FollowBtn state={state} following={false} busy={busy} setBusy={setBusy} onDone={(f) => setData({ ...data, following: f })} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-4">
      <div className="bg-charcoal text-bone rounded-xl px-5 sm:px-7 py-5" data-testid="your-state-panel">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <MapPin size={15} className="text-blaze" />
            <span className="hr-overline text-blaze text-[11px]">Your {displayState}</span>
          </div>
          <FollowBtn state={state} following={data.following} busy={busy} setBusy={setBusy} onDone={(f) => setData({ ...data, following: f })} />
        </div>
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
          {data.points.map((p) => (
            <div key={p.species} className="flex items-center gap-2" data-testid={`your-state-points-${p.species.toLowerCase()}`}>
              <Target size={14} className="text-forest" />
              <span className="text-bone/70 text-sm">{p.species} points</span>
              <span className="font-serif text-xl text-bone">{p.points}</span>
            </div>
          ))}
          {data.upcoming_hunts > 0 && (
            <div className="flex items-center gap-2"><CalendarClock size={14} className="text-blaze" /><span className="text-sm text-bone/70">{data.upcoming_hunts} upcoming hunt{data.upcoming_hunts === 1 ? "" : "s"}</span></div>
          )}
          {data.past_hunts > 0 && (
            <div className="flex items-center gap-2"><Star size={14} className="text-bone/50" /><span className="text-sm text-bone/70">{data.past_hunts} past hunt{data.past_hunts === 1 ? "" : "s"}</span></div>
          )}
        </div>
      </div>
    </div>
  );
}

function FollowBtn({ state, following, busy, setBusy, onDone }) {
  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.toggleFollowState(state);
      onDone(r.following);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid="your-state-follow"
      className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 transition-colors ${following ? "bg-forest text-bone hover:bg-forest/80" : "bg-blaze text-white hover:bg-blaze-light"} disabled:opacity-50`}>
      <Star size={14} className={following ? "fill-current" : ""} /> {following ? "Following" : `Follow ${state}`}
    </button>
  );
}
